import React, { useEffect, useState } from 'react'
import Text from '../../../../../components/Text'
import BottomSheet from '../../../../../components/BottomSheet'
import useDoctorsLocation from '../../../../AnayaCare/hooks/useDoctorsLocation'
import Spinner from '../../../../../components/Spinner'
import ChevronDownIcon from '../../../../../components/Icons/ChevronRight'
import SearchIcon from '../../../../../components/Icons/SearchIcon'
import ProceedButton from '../../../../../components/Signup/ProceedButton'
import InputField from '../../../../../components/InputField'
import * as actions from '../../../../../store/slices'
import { useDispatch, useSelector } from 'react-redux'
import SectionHeader from '../SectionHeader'
import { trackEvent } from '../../../../../analytics'
import { EVENTS } from '../../../../../analytics/events'

const PatientAddress = ({
  onChange,
  canSkip,
  onSkip,
  pageObj,
  onMoveNext,
  initialSelection,
  initialFormFields,
}) => {
  const dispatch = useDispatch()
  const {
    list,
    selectedState,
    selectedCity,
    activeSection,
    handleOnCloseSheet,
    searchQuery,
    handleOnSearch,
    handleActiveSession,
    handleOptionClick,
    loading,
    loadMoreCities,
  } = useDoctorsLocation({ onChange, initialSelection })
  const { updatePatientInfoState } = useSelector((s) => s.auth)
  const { userDetailsState } = useSelector((s) => s.authentication)
  const patientDetails = userDetailsState?.data || {}
  const submitting = updatePatientInfoState?.loading

  const [formFields, setFormFields] = useState({
    pincode: initialFormFields?.pincode || '',
    address: initialFormFields?.address || '',
  })

  const onFormFieldChange = (key, value) => {
    setFormFields({ ...formFields, [key]: value })
  }

  // Handle infinite scroll
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100

    if (isNearBottom && activeSection === 'city' && !loading) {
      loadMoreCities()
    }
  }

  const handleFinish = () => {
    const payload = {
      ...formFields,
      state: selectedState?.state_name,
      city: selectedCity?.city_name,
      patient_id: patientDetails?.patientID,
    }
    // console.log('payload', payload)
    trackEvent(EVENTS.MY_SAATHI_ADDRESS_CONTINUE_CLICKED, {
      screenName: 'Onboarding Screen',
      address: JSON.stringify(payload),
    })
    dispatch(actions.updatePatientInformationRequest(payload))
  }

  useEffect(() => {
    if (!submitting && updatePatientInfoState?.success) {
      onMoveNext(false, {
        stepType: 'address',
        selectedState,
        selectedCity,
        formFields,
      })
      dispatch(actions.resetUpdatePatientInfoState())
    }
  }, [updatePatientInfoState])

  return (
    <div className="pt-5 px-4">
      {/* Section Header */}
      <SectionHeader pageObj={pageObj} />

      {/* Form */}
      <div className="flex flex-col gap-5 mt-6 pb-6">
        {/* Patient's state */}
        <div onClick={() => handleActiveSession('state')}>
          <InputField
            name="state"
            label="State"
            type="text"
            placeholder="Select State"
            value={selectedState?.state_name}
            readOnly={true}
            disabled={submitting}
            rightIcon={
              loading ? <Spinner /> : <ChevronDownIcon className="rotate-90" />
            }
          />
        </div>

        {/* Patient's city */}
        <div
          onClick={() =>
            selectedState?.state_name && handleActiveSession('city')
          }
        >
          <InputField
            name="city"
            label="City"
            type="text"
            placeholder="Select City"
            value={selectedCity?.city_name}
            readOnly={true}
            disabled={submitting}
            rightIcon={
              loading ? <Spinner /> : <ChevronDownIcon className="rotate-90" />
            }
          />
        </div>

        {/* Patient's Pincode */}
        <div>
          <InputField
            name="pincode"
            label="Pincode"
            type="number"
            inputMode="numeric"
            maxLength={6}
            minLength={6}
            placeholder="Enter Pincode"
            disabled={submitting}
            onChange={(e) => {
              if (/^\d{0,6}$/.test(e.target.value)) {
                onFormFieldChange('pincode', e.target.value)
              }
            }}
            value={formFields?.pincode}
          />
        </div>

        {/* Patient's Address */}
        <div>
          <InputField
            name="address"
            label="Address"
            type="text"
            placeholder="Enter Address"
            disabled={submitting}
            onChange={(e) => onFormFieldChange('address', e.target.value)}
            value={formFields.address}
          />
        </div>

        <ProceedButton
          onMoveNext={handleFinish}
          isLastStep={false}
          disabled={
            !selectedState?.state_name ||
            !selectedCity?.city_name ||
            !formFields?.pincode.length === 6 ||
            !formFields?.address ||
            updatePatientInfoState?.loading
          }
          canSkip={canSkip}
          onSkip={onSkip}
        />
      </div>

      {/* state list */}
      <BottomSheet
        show={Boolean(activeSection)}
        onClose={handleOnCloseSheet}
        title={`Select ${activeSection}`}
      >
        <div className="min-h-[80vh] max-h-[80vh] px-4 pt-5 space-y-5">
          <div>
            <InputField
              name="state"
              type="text"
              placeholder={`Search your ${activeSection}`}
              onChange={handleOnSearch}
              value={searchQuery}
              disabled={loading}
              rightIcon={
                loading ? (
                  <Spinner />
                ) : (
                  <div className="relative h-full w-full">
                    <SearchIcon />
                  </div>
                )
              }
            />
          </div>
          <div
            className="max-h-[67vh] overflow-y-auto px-2 pb-3"
            onScroll={handleScroll}
          >
            {loading && list?.length === 0 && (
              <div className="flex justify-center items-center h-10">
                <Text
                  tag="p"
                  variant="body"
                  size="large"
                  className="text-[#6C7278]"
                >
                  Loading...
                </Text>
              </div>
            )}
            <ul className="space-y-4">
              {list?.map((item, index) => (
                <li key={index} onClick={() => handleOptionClick(item)}>
                  <Text
                    tag="p"
                    variant="body"
                    size="large"
                    className="text-[#667085]"
                  >
                    {activeSection === 'state'
                      ? item?.state_name
                      : activeSection === 'city'
                        ? item?.city_name
                        : activeSection === 'hospital'
                          ? item?.clinic_name
                          : item?.name}
                  </Text>
                </li>
              ))}
            </ul>
            {/* Loading more indicator for pagination */}
            {loading && list?.length > 0 && activeSection === 'city' && (
              <div className="flex justify-center items-center py-4">
                <Spinner />
              </div>
            )}
            {list?.length === 0 && !loading && (
              <div className="flex h-10">
                <Text
                  tag="p"
                  variant="body"
                  size="large"
                  className="text-[#6C7278]"
                >
                  No data found
                </Text>
              </div>
            )}
          </div>
        </div>
      </BottomSheet>
    </div>
  )
}

export default PatientAddress
